import { useState } from 'react'
import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Pencil, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import type { DragEndEvent } from '@dnd-kit/core'

import type { ShopGuideVideo } from '@/lib/api/shop-guide-videos-api'
import { GuideVideoFormDialog } from '@/components/retail/guide-videos/guide-video-form-dialog'
import {
    formatGuideDuration,
    groupGuideVideos,
} from '@/components/retail/guide-videos/guide-video-utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    useDeleteShopGuideVideo,
    useReorderShopGuideVideos,
    useSaveShopGuideVideo,
    useShopGuideVideosManage,
} from '@/hooks/use-shop-guide-videos-query'
import { toGuideVideoInput } from '@/lib/api/shop-guide-videos-api'
import { m } from '@/lib/paraglide/messages'

/**
 * Tab "Quản lý" (shop_content.write): mọi video kể cả đang ẩn. Kéo thả là lưu
 * thứ tự (gửi đủ id); bật / tắt hiện ngay trên dòng; sửa / thêm trong hộp.
 */
export function GuideVideoManager() {
    const query = useShopGuideVideosManage()
    const save = useSaveShopGuideVideo()
    const remove = useDeleteShopGuideVideo()
    const reorder = useReorderShopGuideVideos()
    const [order, setOrder] = useState<string[] | null>(null)
    const [editing, setEditing] = useState<ShopGuideVideo | 'new' | null>(null)

    const server = query.data?.data ?? []
    const byId = new Map(server.map((v) => [v.id, v]))
    const rows = order
        ? order.flatMap((id) => (byId.has(id) ? [byId.get(id)!] : []))
        : server
    const grouped = groupGuideVideos(server)
    const groups = grouped.map((g) => g.name)
    const groupCounts = Object.fromEntries(
        grouped.map((g) => [g.name, g.items.length]),
    )

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    )

    const onDragEnd = (e: DragEndEvent) => {
        if (!e.over || e.active.id === e.over.id) return
        const ids = rows.map((r) => r.id)
        const next = arrayMove(
            ids,
            ids.indexOf(String(e.active.id)),
            ids.indexOf(String(e.over.id)),
        )
        setOrder(next)
        reorder.mutate(next, {
            onSuccess: () => toast.success(m.guideVideo_reordered()),
            onSettled: () => setOrder(null),
        })
    }

    const toggle = (v: ShopGuideVideo) =>
        save.mutate(
            { id: v.id, input: { ...toGuideVideoInput(v), active: !v.active } },
            { onError: () => toast.error(m.guideVideo_toggle_failed()) },
        )

    const del = (v: ShopGuideVideo) => {
        if (!window.confirm(m.guideVideo_delete_confirm({ title: v.title })))
            return
        remove.mutate(v.id, {
            onSuccess: () => toast.success(m.guideVideo_deleted()),
        })
    }

    return (
        <div className="space-y-3">
            <div className="flex justify-end">
                <Button size="sm" onClick={() => setEditing('new')}>
                    <Plus className="mr-1 size-4" />
                    {m.guideVideo_add()}
                </Button>
            </div>
            <div className="overflow-x-auto rounded-md border">
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={onDragEnd}
                >
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-8" />
                                <TableHead>
                                    {m.guideVideo_col_title()}
                                </TableHead>
                                <TableHead>
                                    {m.guideVideo_col_group()}
                                </TableHead>
                                <TableHead className="text-right">
                                    {m.guideVideo_col_duration()}
                                </TableHead>
                                <TableHead className="text-center">
                                    {m.guideVideo_col_active()}
                                </TableHead>
                                <TableHead className="w-20" />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {query.isLoading ? (
                                <MessageRow text={m.common_loading()} />
                            ) : query.isError ? (
                                <MessageRow
                                    text={m.guideVideo_load_failed()}
                                    className="text-destructive"
                                />
                            ) : rows.length === 0 ? (
                                <MessageRow
                                    text={m.guideVideo_empty_manage()}
                                    className="py-8 text-center text-muted-foreground"
                                />
                            ) : (
                                <SortableContext
                                    items={rows.map((r) => r.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {rows.map((v) => (
                                        <ManageRow
                                            key={v.id}
                                            video={v}
                                            busy={remove.isPending}
                                            onEdit={() => setEditing(v)}
                                            onToggle={() => toggle(v)}
                                            onDelete={() => del(v)}
                                        />
                                    ))}
                                </SortableContext>
                            )}
                        </TableBody>
                    </Table>
                </DndContext>
            </div>
            <GuideVideoFormDialog
                editing={editing}
                groups={groups}
                groupCounts={groupCounts}
                onClose={() => setEditing(null)}
            />
        </div>
    )
}

function MessageRow({ text, className }: { text: string; className?: string }) {
    return (
        <TableRow>
            <TableCell
                colSpan={6}
                className={className ?? 'text-muted-foreground'}
            >
                {text}
            </TableCell>
        </TableRow>
    )
}

function ManageRow({
    video,
    busy,
    onEdit,
    onToggle,
    onDelete,
}: {
    video: ShopGuideVideo
    busy: boolean
    onEdit: () => void
    onToggle: () => void
    onDelete: () => void
}) {
    const { attributes, listeners, setNodeRef, transform, transition } =
        useSortable({ id: video.id })
    return (
        <TableRow
            ref={setNodeRef}
            style={{ transform: CSS.Transform.toString(transform), transition }}
        >
            <TableCell className="w-8 px-1">
                <button
                    type="button"
                    className="flex size-8 cursor-grab touch-none items-center justify-center rounded text-muted-foreground hover:bg-muted"
                    aria-label={m.guideVideo_drag_aria({ title: video.title })}
                    {...attributes}
                    {...listeners}
                >
                    <GripVertical className="size-4" />
                </button>
            </TableCell>
            <TableCell className="min-w-56">
                <div className="font-medium">{video.title}</div>
                {!video.active && (
                    <Badge variant="secondary" className="mt-1">
                        {m.guideVideo_hidden_badge()}
                    </Badge>
                )}
            </TableCell>
            <TableCell className="min-w-40 text-muted-foreground">
                {video.group_name}
            </TableCell>
            <TableCell className="text-right tabular-nums">
                {formatGuideDuration(video.duration_seconds)}
            </TableCell>
            <TableCell className="text-center">
                <Switch
                    checked={video.active}
                    onCheckedChange={onToggle}
                    aria-label={m.guideVideo_active_aria({
                        title: video.title,
                    })}
                />
            </TableCell>
            <TableCell>
                <div className="flex justify-end gap-1">
                    <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        aria-label={m.guideVideo_edit_aria({
                            title: video.title,
                        })}
                        onClick={onEdit}
                    >
                        <Pencil className="size-4" />
                    </Button>
                    <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="text-destructive"
                        disabled={busy}
                        aria-label={m.guideVideo_delete_aria({
                            title: video.title,
                        })}
                        onClick={onDelete}
                    >
                        <Trash2 className="size-4" />
                    </Button>
                </div>
            </TableCell>
        </TableRow>
    )
}
